import { z } from "zod";

export type ContractMessages = {
  invalid: string;
  /** Shown for a blank value instead of `invalid`. */
  required?: string;
  /** Shown when the value exceeds the contract's maximum length. */
  tooLong?: string;
};

/**
 * Wraps a shared API contract schema so it keeps the contract's validation and
 * normalization, but reports failures with a localized message.
 *
 * Client-side forms must not restate the contract's rules, otherwise the two
 * drift apart; they only own the wording shown to the user.
 *
 * A blank input reaches the contract as `undefined`: an empty text field means
 * "not given", so an optional contract such as `z.optional(…)` accepts it.
 */
const localize =
  <Output>(
    contractSchema: z.ZodType<Output>,
    { invalid, required, tooLong }: ContractMessages,
  ) =>
  (value: string | null, context: z.RefinementCtx): Output => {
    const isBlank = value === null || value.trim().length === 0;
    const parsedValue = contractSchema.safeParse(isBlank ? undefined : value);

    if (!parsedValue.success) {
      const isTooLong = parsedValue.error.issues.some(
        (issue) => issue.code === "too_big",
      );

      context.addIssue({
        code: "custom",
        message:
          isBlank && required
            ? required
            : isTooLong && tooLong
              ? tooLong
              : invalid,
        continue: true,
      });
      return z.NEVER;
    }

    return parsedValue.data;
  };

export const localizeContractSchema = <Output>(
  contractSchema: z.ZodType<Output>,
  messages: ContractMessages,
) => z.string().transform(localize(contractSchema, messages));

/**
 * Like `localizeContractSchema`, for a field whose empty state is `null`
 * instead of an empty string, such as an unselected option of a select. The
 * contract still receives `undefined` for it.
 */
export const localizeSelectContractSchema = <Output>(
  contractSchema: z.ZodType<Output>,
  messages: ContractMessages,
) => z.string().nullable().transform(localize(contractSchema, messages));
